<script>
   /****************************************************************/
   /* ScoresPlot                                                   */
   /* --------------------                                         */
   /* shows plot with PCA scores                                   */
   /****************************************************************/

   import { Axes, XAxis, YAxis, Box, ColormapLegend, Segments } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';

   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, AXIS_COLOR, MAX_LABEL_SYMBOLS } from './Defaults.js';
   import { getAxisTitle, getColorGroups } from './Defaults.js';

   import PlotSeries from './PlotSeries.svelte';
   import HotellingEllipse from './HotellingEllipse.svelte';
   import ConfidenseEllipses from './ConfidenseEllipses.svelte';
   import { pcares_get } from '../wasm/dist/pca.js';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let params;                        // JSON with main plot parameters
   export let res;                           // JSON with result objects (PCA, PLS, etc)
   export let dataset;                       // current dataset
   export let resID = 'cal';                 // ID of results ('cal', 'pv', 'test')
   export let alpha = 0.05;                  // confidence level for Hotelling ellipse

   // saving parameters
   export let fileName = 'plot-scores';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;

   // outputs
   export let selected = undefined;          // selected object or variable


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const showGrid = true;


   /**
    * Event handler to select a data point.
    * @param e - event object.
    */
   function select(e) {
      if (selected === undefined) return; // do not select points if this option is not activated in parent app
      const id = parseInt(e.detail.elementID)
      const series_title = e.detail.seriesTitle
      if (id >= 0 && (series_title === 'scores-points' || series_title === 'scores-bars')) {
         selected = id;
      }
   }

   /**
    * Event handler to remove selection.
    * @param e - event object.
    */
   function resetSelection(e) {
      selected = -1;
   }

   // ranges of values to define axis limits
   let xMinS, yMinS, xMaxS, yMaxS;
   let xMinE, yMinE, xMaxE, yMaxE;
   let xMinCE, yMinCE, xMaxCE, yMaxCE;

   // local copies of changeable plot parameters
   let comp = [1, 1];
   let plotType = 'p';
   let showLabels = 'hide';
   let showEllipse = 'hide';
   let colorBy = 'none';
   let colorByVar = 1;
   let confEllipse = 'none';

   // reactive expressions to monitor changes in plot parameters
   $: if (params && params.comp !== comp) comp = params.comp;
   $: if (params && params.plotType !== plotType) plotType = params.plotType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.showEllipse !== showEllipse) showEllipse = params.showEllipse;
   $: if (params && params.colorBy !== colorBy) colorBy = params.colorBy;
   $: if (params && params.colorByVar !== colorByVar) colorByVar = params.colorByVar;
   $: if (params && params.confEllipse !== confEllipse) confEllipse = params.confEllipse;


   /**
    * Gets vector with values for particular outcome and component from the result object
    * @param {Object} r - result object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(r, a) {
      return new Vector(pcares_get(r, 'scores', a))
   }

   // reactive expression to generate x-indices for line and bar plots
   let rowIndices, labels
   $: if (res && res.nrows > 0) {
      rowIndices = vector(res.objindices);
      labels = res.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS));
   }

   // reactive expression to track chenges in "res" and "comp[0]" (x-axis)
   let compX = 1, compXValues = null;
   $: if (comp[0] !== compX) compX = comp[0];
   $: compXValues = res ? getValues(res, compX) : null;

   // reactive expression to track chenges in "reobj" and "comp[1]" (y-axis)
   let compY = 1, yValues = null;
   $: if (comp[1] !== compY) compY = comp[1];
   $: yValues = res ? getValues(res, compY) : null;

   // switch between scores and indices for x-axis
   $: xValues = plotType === 'p' ? compXValues : rowIndices;

   // reactive expression to get colors of the elements
   let collabels, colmap, colbreaks, objcolors, colorVarName;
   $: [collabels, colmap, colbreaks, objcolors, colorVarName] = getColorGroups(dataset, res, colorBy, colorByVar, resID);

   // reactive expression to generate axis labels
   let xLabel, yLabel;
   $: if (res && comp) {
      xLabel = plotType === 'p' ? getAxisTitle(res, comp[0]) : 'Objects';
      yLabel = getAxisTitle(res, comp[1]);
   } else {
      xLabel = '';
      yLabel = '';
   }
   // reactive expression to compute y-axis limits
   let limY, limX;
   $: if (xMinS !== undefined && typeof xMaxS !== undefined && typeof yMinS !== undefined && typeof yMaxS !== undefined) {

      // compute ranges for score values
      let yMin = yMinS;
      let yMax = yMaxS;
      let xMin = xMinS;
      let xMax = xMaxS;

      // adjust ranges if T2 ellipse is shown
      if (showEllipse === 'show' && xMinE && xMaxE && yMinE && yMaxE) {
         yMin = Math.min(yMin, yMinE);
         yMax = Math.max(yMax, yMaxE);
         xMin = Math.min(xMin, xMinE);
         xMax = Math.max(xMax, xMaxE);
      }

      // adjust ranges if confidence ellipses are shown
      if (confEllipse !== 'none' && xMinCE && xMaxCE && yMinCE && yMaxCE) {
         yMin = Math.min(yMin, yMinCE);
         yMax = Math.max(yMax, yMaxCE);
         xMin = Math.min(xMin, xMinCE);
         xMax = Math.max(xMax, xMaxCE);
      }
      // compute limits based on the ranges
      const dX = (xMax - xMin) * 0.10;
      const dY = (yMax - yMin) * 0.10;
      limX = [xMin - dX, xMax + dX];
      limY = [yMin - dY, yMax + dY * (1 + (colorBy !== 'none'))];
   } else {
      limX = [0, 1];
      limY = [0, 1];
   }

   // reactive expression to generate plot title
   let title = 'Scores';
   $: if (plotType !== 'l' && colorBy !== 'none') {
      const colorVarLabel = colorVarName + (dataset.varvaluesUnits ? ' ' + dataset.varvaluesUnits : '');
      title = colorBy ==='groups' ? 'Scores (colored by groups)' : 'Scores (colored by ' + colorVarLabel + ')';
   } else {
      title = 'Scores';
   }
</script>

{#if res }
<div class="plot-container">
<Axes
   {title} {xLabel} {yLabel} {limX} {limY}
   {fileName} {pngWidth} {pngHeight} {pngRes}
   on:markerclick={select}
   on:barclick={select}
   on:axesclick={resetSelection}
   downloadLinks="hover"
   margins={MARGINS}
>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- T2 ellipse -->
   {#if showEllipse === 'show' && plotType === 'p'}
   <HotellingEllipse
      {alpha}
      {comp}
      resobj={res}
      bind:minYVal={yMinE}
      bind:maxYVal={yMaxE}
      bind:minXVal={xMinE}
      bind:maxXVal={xMaxE}
   />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {plotType}
      {showLabels}
      {labels}
      {selected}
      seriesTitle="scores"
      colors={objcolors}
      bind:minYVal={yMinS}
      bind:maxYVal={yMaxS}
      bind:minXVal={xMinS}
      bind:maxXVal={xMaxS}
   />


   {#if colorBy === 'groups' && confEllipse !== 'none' && colmap}
   <ConfidenseEllipses
      {xValues}
      {yValues}
      confLevel={Number.parseInt(confEllipse)/100}
      groups={res.classes}
      groupNames={res.classNames}
      groupColors={colmap}
      bind:minYVal={yMinCE}
      bind:maxYVal={yMaxCE}
      bind:minXVal={xMinCE}
      bind:maxXVal={xMaxCE}
   />
   {/if}

   <!-- color by legend-->
   {#if colorBy !== 'none' && plotType !== 'l' && colbreaks}
   <ColormapLegend {colmap} breaks={colbreaks} labels={collabels}/>
   {/if}

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
</div>
{#if selected !== undefined && plotType !== 'l'}
   {#if selected < 0}
   <div class="msg-select">Click on a point to select...</div>
   {:else}
   <div class="msg-select">Click anywhere to remove selection...</div>
   {/if}
{/if}
{/if}

<style>
   .plot-container {
      width: 100%;
      height: calc(100% - 15px);
   }
   .msg-select {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>